import { createStore } from "vuex";
import axios from "axios";
import { createLogger } from "../utils/logger";

const logger = createLogger("Store");
const getPortalContext = () => {
  if (
    window.location.hostname.startsWith("staff.") ||
    window.location.pathname.startsWith("/staff")
  )
    return "staff";
  return "portal";
};

export default createStore({
  state: {
    session: null,
    accessToken: null,
    authChecked: false,
    isLoading: true,
  },
  getters: {
    session: (state) => state.session,
    getUser: (state) => state.session?.user || null,
    getApplication: (state) => state.session?.application || null,
    getStudent: (state) => state.session?.student || null,
    getStaff: (state) => state.session?.staff || null,
    isSuperAdministrator: (state) =>
      state.session?.authorization?.isSuperAdministrator === true,
    getAccessToken: (state) => state.accessToken,
    isAuthenticated: (state) =>
      Boolean(state.session?.user && state.session.user.role !== "staff"),
    isStaffAuthenticated: (state) => state.session?.user?.role === "staff",
    isLoading: (state) => state.isLoading,
    userContext: () => getPortalContext(),
    hasModule: (state) => (module) => {
      const role = state.session?.staff?.accessRoleId;
      return (
        role?.key === "super_administrator" ||
        role?.modulePermissions?.some((item) => item.module === module) ||
        false
      );
    },
    hasPermission: (state) => (module, action) => {
      const role = state.session?.staff?.accessRoleId;
      if (role?.key === "super_administrator") return true;
      const entry = role?.modulePermissions?.find(
        (item) => item.module === module
      );
      return (
        entry?.permissions?.includes("manage") ||
        entry?.permissions?.includes(action) ||
        false
      );
    },
  },
  mutations: {
    setSession(state, session) {
      state.session = session;
      state.isLoading = false;
    },
    setAccessToken(state, token) {
      state.accessToken = token;
      if (token)
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      else delete axios.defaults.headers.common.Authorization;
    },
    setLoading(state, loading) {
      state.isLoading = loading;
    },
    setAuthChecked(state, value) {
      state.authChecked = value;
    },
    clearAuthentication(state) {
      state.session = null;
      state.accessToken = null;
      state.authChecked = true;
      state.isLoading = false;
      delete axios.defaults.headers.common.Authorization;
    },
  },
  actions: {
    async refreshAccessToken({ commit }) {
      const response = await axios.post(
        "/auth/refresh",
        {},
        { withCredentials: true }
      );
      const token = response.data?.data?.accessToken;
      if (!token)
        throw new Error("Refresh response did not contain an access token");
      commit("setAccessToken", token);
      return token;
    },
    async refreshSession({ commit }) {
      const response = await axios.get("/auth/session", {
        withCredentials: true,
      });
      const session = response.data?.data;
      if (!session?.user) throw new Error("Session context is unavailable");
      commit("setSession", session);
      return session;
    },
    async checkAuthStatus({ commit, dispatch }) {
      commit("setLoading", true);
      try {
        await dispatch("refreshAccessToken");
        const session = await dispatch("refreshSession");
        const isStaffPortal = getPortalContext() === "staff";
        if (
          (isStaffPortal && session.user.role !== "staff") ||
          (!isStaffPortal && session.user.role === "staff")
        ) {
          throw new Error("This account belongs on a different portal");
        }
      } catch (error) {
        logger.debug("No active authentication session", error);
        commit("clearAuthentication");
      } finally {
        commit("setAuthChecked", true);
        commit("setLoading", false);
      }
    },
    async logout({ commit }) {
      try {
        await axios.post("/auth/logout", {}, { withCredentials: true });
      } finally {
        commit("clearAuthentication");
      }
    },
    async staffLogout({ dispatch }) {
      return dispatch("logout");
    },
    async refreshUserData({ dispatch }) {
      return dispatch("refreshSession");
    },
  },
});
